import * as THREE from 'three';
import {
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  Points,
  ShaderLib,
  ShaderMaterial,
  Vector3,
} from 'three';

import circlePointsFragmentShader from '../../../static/glsl/points/circle-points.frag';
import crossPointsFragmentShader from '../../../static/glsl/points/cross-points.frag';
import plusPointsFragmentShader from '../../../static/glsl/points/plus-points.frag';
import roundPointsFragmentShader from '../../../static/glsl/points/round-points.frag';
import squarePointsFragmentShader from '../../../static/glsl/points/square-points.frag';
import trianglePointsFragmentShader from '../../../static/glsl/points/triangle-points.frag';
import { renderOrders } from '../render-orders';

type PointStyle = 'round' | 'triangle' | 'cross' | 'circle' | 'square' | 'plus';

export class PickingSphere extends Points {
  constructor(color?: THREE.Color, size: number = 10, pointStyle: PointStyle = 'round') {
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute([0, 0, 0], 3));

    super(
      geometry,
      new ShaderMaterial({
        transparent: true,
        depthTest: false,
        depthWrite: false,
        opacity: 1.0,
        uniforms: {
          size: { value: size },
          scale: { value: 1 },
          color: { value: color ?? new Color(0, 1, 0) },
        },
        vertexShader: ShaderLib.points.vertexShader,
        fragmentShader:
          pointStyle === 'triangle'
            ? trianglePointsFragmentShader
            : pointStyle === 'cross'
              ? crossPointsFragmentShader
              : pointStyle === 'circle'
                ? circlePointsFragmentShader
                : pointStyle === 'plus'
                  ? plusPointsFragmentShader
                  : pointStyle === 'square'
                    ? squarePointsFragmentShader
                    : roundPointsFragmentShader,
      }),
    );

    this.renderOrder = renderOrders.pickers.pickingSpere;
  }

  setNonIndexedVertices(vertices: Vector3[]) {
    this.geometry.setIndex(null);
    this.geometry.setAttribute(
      'position',
      new Float32BufferAttribute(vertices.map((x) => x.toArray()).flat(), 3),
    );
    this.geometry.computeBoundingSphere();
    this.updateMatrix();
  }

  setIndicesAndVertices(position: Float32BufferAttribute, indices?: number[]) {
    if (indices) {
      this.geometry.setIndex(indices);
    } else if (this.geometry.getIndex() !== null) {
      this.geometry = this.geometry.toNonIndexed();
    }

    this.geometry.setAttribute('position', position);

    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.computeBoundingBox();
    this.geometry.computeBoundingSphere();
    this.updateMatrix();
  }

  setColor(color: Color) {
    (this.material as ShaderMaterial).uniforms.color.value = color;
    (this.material as ShaderMaterial).uniformsNeedUpdate = true;
  }
}
